import React, { useEffect, useState } from 'react';
import { object, string } from 'prop-types';
import { connect } from 'react-redux';

import moment from 'moment';
import { isArray, isEmpty, uniq, cloneDeep } from 'lodash';

import queryString from 'query-string';

import MaterialTable from 'material-table';
import { TablePagination } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';

import EsmsPage from '../../components/EsmsPage';
import AgentLogDataFilters from './AgentLogDataFilters';

import logDescriptionTable from '../../helpers/logNamesAndDescriptions';
import { getTimeZoneAbbr } from '../../helpers/timezones';
import * as CoreActions from '../../actions/coreActions';
import * as IotHubActions from '../../actions/iotHubActions';

import classes from './AgentLogDataPage.module.css';

const AgentLogDataPage = ({
    companyInfo,
    fetchLogEntries,
    history,
    location,
    logEndTimeStamp,
    logEntries,
    logSearchValue,
    logStartTimeStamp,
    metaLogData,
    resetAgentLogFilters,
    resetCounters,
    selectedLog,
    setProgressBarLoading,
    ...props
}) => {
    const queryValues = queryString.parse(history.location.search);

    const [ filteredLogEntries, setFilteredLogEntries ] = useState([]);
    const [ page, setPage] = useState(0);
    const [ pageSize, setPageSize] = useState(100);
    const [ isLoading, setIsLoading] = useState(false);
    const [ gridData, setGridData] = useState([]);

    useEffect(() => {
        if (!isEmpty(filteredLogEntries)) {
            const startLocation = page === 0 ? 0 : pageSize * page;
            setGridData(cloneDeep(filteredLogEntries).slice(startLocation, startLocation + pageSize));
        }
        else {
            setGridData([]);
        }
    }, [filteredLogEntries, page, pageSize]);

    useEffect(() => {
        if (!selectedLog || isEmpty(selectedLog)) {
            handleAdvancedLogRoute();

            return;
        }

        logEntryRefresh();

        return () => {
            // Clean up log entries and filter settings so next log view
            // is pristine.
            setFilteredLogEntries();
            resetAgentLogFilters();
            resetCounters();
            fetchLogEntries();
        };
    }, []);

    useEffect(() => {
        if (isArray(logEntries)) {
            setFilteredLogEntries(logEntries);
        }
    }, [logEntries]);

    useEffect(() => {
        switch (selectedLog.machineName) {
            case 'auth':
            case 'syslog':
                handleLocalSearch(logSearchValue);
                break;
            default:
                // If selectedLog is empty we know this is kicking off due to
                // a page refresh, so we skip calling this method.
                // Also if filteredLogEntries is empty don't run method bc
                // the page is initializing.
                if (!isEmpty(selectedLog) && filteredLogEntries.length) {
                    // Search is server driven for all but auth and syslog, so
                    // now that redux has been updated we refresh the
                    // log entry collection with the search term.
                    logEntryRefresh();
                }
        }
    }, [logSearchValue]);

    const logEntryRefresh = async () => {
        const options = {
            companyId:     companyInfo.companyId,
            serialNumber:  selectedLog.serialNumber,
            logGroup:      selectedLog.machineName,
            startDateTime: logStartTimeStamp.toISOString(),
            endDateTime:   logEndTimeStamp.toISOString(),
            filterPattern: logSearchValue
        };
        setProgressBarLoading(true);
        setIsLoading(true);
        await fetchLogEntries(options);
        setPage(0);
        setIsLoading(false);
        setProgressBarLoading(false);
    };

    const logEntryReset = () => {
        resetAgentLogFilters();
        
        logEntryRefresh();
        setPage(0);
    };

    const handlePageChange = (event, page) => {
        setPage(page);
    };

    const handlePageSizeChange = event => {
        const { value } = event.target;

        setPageSize(value);
    };
    const convertLocalTime = rowData => {
        const zone = moment.tz.guess();
        const convertedTime = moment(rowData.dateTime).format('YYYY-MM-DD h:mm:ss.SS A') + ' ' + getTimeZoneAbbr(rowData.dateTime, zone);

        return convertedTime;
    };

    const handleIotHubRoute = () => {
        history.push(`/iothub?company=${queryValues.company}`);
    };

    const handleSelectedAgentRoute = () => {
        history.push(`/iothub/basagents/manage?company=${queryValues.company}&basAgent=${queryValues.basAgent}`);
    };

    const handleAdvancedLogRoute = () => {
        history.push(`/iothub/basagents/manage/logs/advanced?company=${queryValues.company}&basAgent=${queryValues.basAgent}`);
    };

    const handleLocalSearch = searchValue => {

        
        if (isEmpty(searchValue)) {
            setFilteredLogEntries(logEntries);
            return;
        }

        const normalizeSearchValue = searchValue.toLowerCase();

        let dateTimeEntries,
            logTypeEntries,
            messageTagEntries,
            messageEntries,
            groupedEntries;

        dateTimeEntries = logEntries
            .filter(entry => !isEmpty(entry.dateTime))
            .filter(entry => entry.dateTime.toLowerCase().includes(normalizeSearchValue));

        logTypeEntries = logEntries
            .filter(entry => !isEmpty(entry.logType))
            .filter(entry => entry.logType.toLowerCase().includes(normalizeSearchValue));

        messageTagEntries = logEntries
            .filter(entry => !isEmpty(entry.messageTag))
            .filter(entry => entry.messageTag.toLowerCase().includes(normalizeSearchValue));

        messageEntries = logEntries
            .filter(entry => !isEmpty(entry.message))
            .filter(entry => entry.message.toLowerCase().includes(normalizeSearchValue));

        groupedEntries = [...dateTimeEntries, ...logTypeEntries, ...messageTagEntries, ...messageEntries];

        setFilteredLogEntries(uniq(groupedEntries));
    };

    const parseDataItem = (field, rowData) => {
        if (rowData.message.includes(field)) {
            const json = rowData.message;
            const first = field === 'stationIp' ? '"' + field + '":"' : '"' + field + '":';
            const last = field === 'stationIp' ? '"' : ',';
            const middle = json.split(first)[1].split(last)[0];

            return middle.charAt(0).toUpperCase() + middle.slice(1);
        }
    };

    return (
        <EsmsPage
            parents={[
                companyInfo.companyName,
                'BAS Agents',
                selectedLog.agentName,
                'View Logs'
            ]}
            handlers={[handleIotHubRoute, handleIotHubRoute, handleSelectedAgentRoute, handleAdvancedLogRoute]}
            title={selectedLog.name || ''}
            subtitle={`${metaLogData.returnRecordCount} of ${metaLogData.recordsMatched} log entries fetched. Max of 2,000 records can be returned.`}
        >
            <div className={classes.AgentLogDataPage}>
                <div className={classes.FilterPanel}>
                    <AgentLogDataFilters
                        data={filteredLogEntries}
                        logName={selectedLog.name}
                        reset={logEntryReset}
                        logEntryRefresh={logEntryRefresh}
                    />
                </div>
                <div className={classes.AgentLogDataPageTable}>
                    <style>
                        {`
                            .MuiTypography-caption {
                                font-size: 1.1rem;
                            }
                            .MuiTablePagination-select {
                                font-size: 1.1rem;
                            }                        
                        `}
                    </style>
                    {
                        !logEntries ?
                            <div className={classes.fullScreenLoader}>
                                <CircularProgress
                                    size={50}
                                />
                            </div> :

                            <MaterialTable
                                options={{
                                    paging:        true,
                                    showTitle:     false,
                                    search:        false,
                                    selection:     false,
                                    toolbar:       false,
                                    sorting:       true,
                                    headerStyle:   { position: 'sticky', top: 0 },
                                    maxBodyHeight: '50rem',
                                    pageSize
                                }}
                                columns={selectedLog.name === 'Heartbeat' ?
                                    [
                                        {
                                            title: '',
                                            field: 'rowCount'
                                        },
                                        {
                                            title:  'Timestamp',
                                            field:  'dateTime',
                                            render: rowData => convertLocalTime(rowData)
                                        },
                                        {
                                            title: 'Log Level',
                                            field: 'logType'
                                        },
                                        {
                                            title:  'Station IP',
                                            field:  '',
                                            render: rowData => parseDataItem('stationIp', rowData)
                                        },
                                        {
                                            title:  'Station Port',
                                            field:  '',
                                            render: rowData => parseDataItem('stationPort', rowData)
                                        },
                                        {
                                            title:  'Trusted Device',
                                            field:  '',
                                            render: rowData => parseDataItem('trustedDevice', rowData)
                                        },
                                        {
                                            title:  'Is Connected to AWS',
                                            field:  '',
                                            render: rowData => parseDataItem('isConnectedToAWS', rowData)
                                        },
                                        {
                                            title: 'Message Tag',
                                            field: 'messageTag'
                                        },
                                        {
                                            title: 'Message',
                                            field: 'message'
                                        }
                                    ]
                                    :
                                    [
                                        {
                                            title: '',
                                            field: 'rowCount'
                                        },
                                        {
                                            title:  'Timestamp',
                                            field:  'dateTime',
                                            render: rowData => convertLocalTime(rowData)
                                        },
                                        {
                                            title: 'Log Level',
                                            field: 'logType'
                                        },
                                        {
                                            title: 'Message Tag',
                                            field: 'messageTag'
                                        },
                                        {
                                            title: 'Message',
                                            field: 'message'
                                        }
                                    ]
                                }
                                data={gridData || []}
                                detailPanel={rowData => {
                                    return (
                                        <div className={classes.messageDetails}>
                                            {rowData.message}
                                            {
                                                selectedLog.name === 'All Logs' &&
                                                <div className={classes.sourceLog}>
                                                    Source log: {logDescriptionTable(rowData.log).name}
                                                </div>
                                            }
                                        </div>
                                    );
                                }}
                                isLoading={isLoading}
                                components={{
                                    Pagination: props => {
                                        return (
                                            <TablePagination
                                                {...props}
                                                count={metaLogData.returnRecordCount}
                                                page={page ? page : 0}
                                                rowsPerPage={pageSize}
                                                rowsPerPageOptions={[100, 200, 500]}
                                                onChangePage={handlePageChange}
                                                onChangeRowsPerPage={event => { props.onChangeRowsPerPage(event); handlePageSizeChange(event); } }
                                            />
                                        );
                                    }
                                }}
                            />
                    }
                </div>
            </div>
        </EsmsPage>
    );
};


AgentLogDataPage.propTypes = {
    companyInfo:    object,
    logSearchValue: string,
    selectedLog:    object,
    title:          string
};

export const mapStateToProps = state => {
    return {
        companyInfo:       state.companyInfo,
        logEntries:        state.iotHub.logEntries,
        logEndTimeStamp:   state.iotHub.logEndTimeStamp,
        logSearchValue:    state.iotHub.logSearchValue,
        logStartTimeStamp: state.iotHub.logStartTimeStamp,
        metaLogData:       state.iotHub.metaLogData,
        selectedLog:       state.iotHub.selectedLog
    };
};

const mapDispatchToProps = {
    ...CoreActions,
    ...IotHubActions
};

export default connect(mapStateToProps, mapDispatchToProps)(AgentLogDataPage);
